import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  CircularProgress,
  Fade,
  Grid
} from "@mui/material";
import { AutoAwesome, Close, CheckCircle, Cancel, SmartToy, Send, School, ChatBubble, Mic, MicOff, VolumeUp, VolumeOff, TheaterComedy, Flight, Work, LocalCafe, Settings } from "@mui/icons-material";
import { TextField as MuiTextField, Menu, MenuItem, Switch } from "@mui/material";
import styled, { keyframes, css } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { connect } from "react-redux"; // [NEW] Connect to Redux
import firebase from "firebase/app";
import "firebase/firestore";
import "firebase/functions";
import "firebase/functions";
import { logCoachInteraction } from "../ai/coachInteractionService";
import { OPENAI_API_KEY } from "../../config/openaiConfig"; // [NEW] Import Key

// --- Styled Components ---

const BRAND_GREEN = '#bfd56c';
const BRAND_DARK = '#0d1b0a';

// Glass Effect utility
const glass = ({
  tint = "rgba(255,255,255,0.7)",
  stroke = "rgba(255,255,255,0.4)",
  blur = "20px"
} = {}) => ({
  background: `linear-gradient(135deg, ${tint}, rgba(255,255,255,0.4))`,
  backdropFilter: `blur(${blur})`,
  WebkitBackdropFilter: `blur(${blur})`,
  border: `1px solid ${stroke}`,
  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
});

const GlassDialogPanel = styled(Box)`
    ${glass()};
    border-radius: 24px;
    padding: 32px;
    position: relative;
    overflow: hidden;
    min-height: 300px;
    display: flex;
    flex-direction: column;
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(255, 0, 0, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(255, 0, 0, 0);
  }
`;

const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.1); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const breathe = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 20px rgba(0, 186, 238, 0.2); }
  50% { transform: scale(1.02); box-shadow: 0 0 30px rgba(0, 186, 238, 0.4); }
  100% { transform: scale(1); box-shadow: 0 0 20px rgba(0, 186, 238, 0.2); }
`;

const listenPulse = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 rgba(255, 0, 0, 0.2); }
  50% { transform: scale(1.05); box-shadow: 0 0 30px rgba(255, 0, 0, 0.4); }
  100% { transform: scale(1); box-shadow: 0 0 0 rgba(255, 0, 0, 0.2); }
`;

const speakFlow = keyframes`
  0% { background-position: 0% 50%; border-radius: 50%; transform: scale(1); }
  25% { border-radius: 60% 40% 30% 70% / 60% 30% 70% 40%; transform: scale(1.05); }
  50% { background-position: 100% 50%; border-radius: 30% 60% 70% 40% / 50% 60% 30% 60%; transform: scale(0.95); }
  75% { border-radius: 40% 60% 30% 70% / 60% 30% 70% 40%; transform: scale(1.05); }
  100% { background-position: 0% 50%; border-radius: 50%; transform: scale(1); }
`;

const loadSpin = keyframes`
  0% { transform: rotate(0deg) scale(1); border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%; }
  50% { transform: rotate(180deg) scale(0.9); border-radius: 60% 40% 30% 70% / 60% 30% 50% 60%; background-filter: hue-rotate(90deg); }
  100% { transform: rotate(360deg) scale(1); border-radius: 40% 60% 70% 30% / 40% 50% 60% 50%; }
`;

const Orb = styled(Box)`
    width: 200px;
    height: 200px;
    border-radius: 50%;
    background: linear-gradient(-45deg, #00baee, #aed036, #00baee, #23d5ab);
    background-size: 400% 400%;
    margin: 0 auto;
    position: relative;
    transition: all 0.5s ease;
    cursor: pointer;

    ${props => props.state === 'idle' && css`
        animation: ${breathe} 4s ease-in-out infinite;
        opacity: 0.8;
    `}

    ${props => props.state === 'listening' && css`
        background: linear-gradient(-45deg, #ff4b1f, #ff9068);
        animation: ${listenPulse} 1.5s ease-in-out infinite;
    `}

    ${props => props.state === 'speaking' && css`
        animation: ${speakFlow} 3s ease infinite;
        box-shadow: 0 0 60px rgba(0, 186, 238, 0.6);
    `}

    ${props => props.state === 'loading' && css`
        background: linear-gradient(-45deg, #ffffff, #e2e8f0, #cbd5e1);
        animation: ${loadSpin} 1.5s linear infinite;
        box-shadow: 0 0 40px rgba(255, 255, 255, 0.8);
    `}
`;

const TranscriptBox = styled(Box)`
    text-align: center;
    min-height: 80px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    background: rgba(255,255,255,0.9);
    border-radius: 16px;
    margin-top: 40px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.1);
    z-index: 20; 
    position: relative;
    max-width: 90%;
`;

const GradientButton = styled(Button)`
    background: linear-gradient(90deg, ${BRAND_GREEN}, #00baee) !important;
    color: ${BRAND_DARK} !important;
    font-weight: 800 !important;
    border-radius: 999px !important;
    padding: 10px 24px !important;
    text-transform: none !important;
    box-shadow: 0 8px 16px rgba(176, 213, 11, 0.3) !important;
    transition: transform 0.2s ease !important;
    &:hover {
        transform: translateY(-2px);
        filter: brightness(1.05);
    }
`;

const SecondaryButton = styled(Button)`
    background: rgba(255,255,255,0.5) !important;
    color: #4b5563 !important;
    border-radius: 999px !important;
    font-weight: 700 !important;
    text-transform: none !important;
    &:hover {
        background: rgba(255,255,255,0.8) !important;
    }
`;

const BrainPulse = styled(motion.div)`
    width: 60px;
    height: 60px;
    background: linear-gradient(135deg, ${BRAND_GREEN}, #00baee);
    border-radius: 50%;
    margin: 0 auto 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 0 30px rgba(0, 186, 238, 0.4);
`;

// --- Main Component ---

const AiCoach = ({
  userId,
  level,
  firstname = "l'élève",
  language = "fr",
  sprint = [],
  isOpen = false,
  onClose = () => { },
  profile, // [NEW] Added profile prop
  autoStart = true,
  zIndex = 12000,
}) => {
  const [suggestion, setSuggestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [promptFr, setPromptFr] = useState("");
  const [promptEn, setPromptEn] = useState("");
  const [promptTutor, setPromptTutor] = useState(""); // [NEW]
  const [lastInteractionId, setLastInteractionId] = useState(null);

  // --- TUTOR MODE STATE ---
  const [mode, setMode] = useState("selection"); // 'selection' | 'suggestion' | 'tutor' | 'roleplay-menu'
  const [chatHistory, setChatHistory] = useState([]);
  const [activeRoleplay, setActiveRoleplay] = useState(null); // { title: string, prompt: string }
  const [userMessage, setUserMessage] = useState("");
  const chatEndRef = useRef(null);

  // Voice State
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [isHandsFree, setIsHandsFree] = useState(false); // [NEW] Loop State

  // [FIX] Refs for Stale Closures in Speech Recognition
  const loadingRef = useRef(loading);
  const isSpeakingRef = useRef(isSpeaking);

  useEffect(() => { loadingRef.current = loading; }, [loading]);
  useEffect(() => { isSpeakingRef.current = isSpeaking; }, [isSpeaking]);

  // Voice Selection State
  const [availableVoices, setAvailableVoices] = useState([]);
  const [selectedVoiceURI, setSelectedVoiceURI] = useState(null);
  const [voiceMenuAnchor, setVoiceMenuAnchor] = useState(null);
  const [usePremiumVoice, setUsePremiumVoice] = useState(true); // [NEW] Toggle state
  const audioPlayerRef = useRef(new Audio()); // [NEW] HTML5 Audio Player

  // Load Voices
  useEffect(() => {
    const loadVoices = () => {
      const voices = window.speechSynthesis.getVoices();
      // Blacklist of known robotic/novelty voices on Mac/Windows
      const BAD_VOICES = ['Agnes', 'Albert', 'Bad News', 'Bahh', 'Bells', 'Boing', 'Bruce', 'Bubbles', 'Cellos', 'Deranged', 'Fred', 'Good News', 'Hysterical', 'Junior', 'Kathy', 'Pipe Organ', 'Princess', 'Ralph', 'Trinoids', 'Vicki', 'Victoria', 'Whisper', 'Zarvox', 'Majestic'];

      // 1. Filter by Language
      let candidates = voices.filter(v =>
        v.lang.startsWith(language === 'fr' ? 'fr' : 'en') &&
        !BAD_VOICES.some(bad => v.name.includes(bad)) // Exclude bad ones
      );

      // 2. Sort to prioritize "Natural", "Neural", "Google", "Enhanced", "Premium"
      candidates.sort((a, b) => {
        const getScore = (voice) => {
          let score = 0;
          if (voice.name.includes('Natural')) score += 5;
          if (voice.name.includes('Neural')) score += 5; // Highest priority
          if (voice.name.includes('Google')) score += 3;
          if (voice.name.includes('Premium')) score += 2;
          if (voice.name.includes('Enhanced')) score += 1;
          return score;
        };
        return getScore(b) - getScore(a);
      });

      setAvailableVoices(candidates);

      // recover saved voice SPECIFIC TO LANGUAGE
      const storageKey = `ai_coach_voice_${language}`; // fr or en
      const saved = localStorage.getItem(storageKey);

      if (saved && candidates.find(v => v.voiceURI === saved)) {
        setSelectedVoiceURI(saved);
      } else if (candidates.length > 0) {
        // Default to first (best)
        setSelectedVoiceURI(candidates[0].voiceURI);
        // Optionally save it as default for this run
        // localStorage.setItem(storageKey, candidates[0].voiceURI);
      } else {
        setSelectedVoiceURI(null);
      }
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
  }, [language]);

  const handleVoiceChange = (voiceURI) => {
    setSelectedVoiceURI(voiceURI);
    const storageKey = `ai_coach_voice_${language}`;
    localStorage.setItem(storageKey, voiceURI);
    setVoiceMenuAnchor(null);

    // Test Speak
    const v = availableVoices.find(x => x.voiceURI === voiceURI);
    if (v) {
      const utt = new SpeechSynthesisUtterance("Test 1 2");
      utt.voice = v;
      window.speechSynthesis.speak(utt);
    }
  };

  // Scroll to bottom of chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory, mode]);

  // --- SPEECH RECOGNITION SETUP ---
  const speechRecognitionRef = useRef(null);
  const silenceTimer = useRef(null); // To detect end of sentence

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = true; // Keep listening until silence logic triggers
      recognition.interimResults = true; // Need real-time feedback
      recognition.lang = language === 'fr' ? 'fr-FR' : 'en-US';

      recognition.onstart = () => setIsListening(true);

      recognition.onend = () => {
        setIsListening(false);
        // If HandsFree and NOT speaking, verify needed?
        // Actually, we control restart via logic, so defaults are fine.
      };

      recognition.onresult = (event) => {
        // [FIX] Strict Guard using REFS (Live values)
        if (isSpeakingRef.current || loadingRef.current) {
          console.warn("Ignored input (AI is busy):", event.results[0][0].transcript);
          return;
        }

        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');

        if (transcript.length < 2) return; // Ignore noise

        console.log("Mic Input:", transcript);
        setUserMessage(transcript);

        if (silenceTimer.current) clearTimeout(silenceTimer.current);

        silenceTimer.current = setTimeout(() => {
          console.log("Silence Detected. Sending...");
          // Check Refs again before sending
          if (isSpeakingRef.current || loadingRef.current) return;
          recognition.stop();
          handleSendMessage(transcript);
        }, 1200);
      };

      speechRecognitionRef.current = recognition;
    }
  }, [language]); // Removed dependency loop, logic handled inside Refs if needed

  const toggleListening = () => {
    if (isListening || isSpeaking || isHandsFree || loading) {
      speechRecognitionRef.current?.stop();
      window.speechSynthesis.cancel();
      audioPlayerRef.current.pause(); // [NEW] Stop OpenAI Audio
      setIsListening(false);
      setIsSpeaking(false);
      setIsHandsFree(false); // FORCE STOP EVERYTHING
      setLoading(false); // [FIX] Force clear stuck loading state
    } else {
      setUserMessage("");
      speechRecognitionRef.current?.start();
      setIsHandsFree(true);
    }
  };

  const lastSpeakTime = useRef(0);
  const currentUtteranceRef = useRef(null); // [FIX] Prevent GC

  // [NEW] Audio Queue System
  const audioQueueRef = useRef([]);
  const isPlayingQueueRef = useRef(false);

  const processAudioQueue = async () => {
    if (isPlayingQueueRef.current || audioQueueRef.current.length === 0) return;

    isPlayingQueueRef.current = true;
    const nextUrl = audioQueueRef.current.shift();

    audioPlayerRef.current.src = nextUrl;
    audioPlayerRef.current.onplay = () => {
      setIsSpeaking(true);
      setUserMessage(""); // [FIX] Force clear transcript when AI speaks
    };

    // Pre-fetch check could go here if we wanted parallel fetch/play

    audioPlayerRef.current.onended = () => {
      setIsSpeaking(false);
      isPlayingQueueRef.current = false;

      if (audioQueueRef.current.length > 0) {
        processAudioQueue();
      } else {
        // Queue finished
        if (isHandsFree) {
          setTimeout(() => {
            setUserMessage("");
            try { speechRecognitionRef.current?.start(); } catch (e) { }
          }, 100);
        }
      }
    };

    try {
      await audioPlayerRef.current.play();
    } catch (e) {
      console.error("Audio Play Error", e);
      isPlayingQueueRef.current = false;
      processAudioQueue(); // Skip execution
    }
  };

  // [NEW] Smart Split & Fetch
  const generateOpenAISpeech = async (fullText) => {
    try {
      console.log("Optimizing Audio: Splitting sentences...");
      // Split by punctuation (. ! ? \n) but keep delimiter
      const sentences = fullText.match(/[^.!?\n]+[.!?\n]+/g) || [fullText];

      // Reset Queue
      audioQueueRef.current = [];
      isPlayingQueueRef.current = false;
      audioPlayerRef.current.pause();

      for (const sentence of sentences) {
        const cleanText = sentence.trim();
        if (cleanText.length < 2) continue;

        const response = await fetch("https://api.openai.com/v1/audio/speech", {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${OPENAI_API_KEY}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            model: "tts-1",
            input: cleanText,
            voice: language === 'fr' ? "shimmer" : "alloy"
          })
        });

        if (!response.ok) throw new Error("TTS Failed");

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);

        audioQueueRef.current.push(url);

        // If nothing is playing, start immediately!
        if (!isPlayingQueueRef.current) {
          processAudioQueue();
        }
      }

      // [FIX] If loop finishes but nothing played (e.g. empty text?), restart mic
      if (!isPlayingQueueRef.current && audioQueueRef.current.length === 0) {
        console.warn("No audio generated from sentences.");
        if (isHandsFree) {
          setTimeout(() => {
            try { speechRecognitionRef.current?.start(); } catch (e) { }
          }, 100);
        }
      }

    } catch (err) {
      console.error("OpenAI TTS Error:", err);
      // Fallback?
    }
  };

  const speakText = (text) => {
    // Prevent Stutter (Debounce duplicate calls)
    const now = Date.now();
    if (now - lastSpeakTime.current < 500) {
      console.warn("Skipping duplicate speak call");
      return;
    }
    lastSpeakTime.current = now;

    // Cancel current speech
    window.speechSynthesis.cancel();
    audioPlayerRef.current.pause();

    // [NEW] CHECK PREMIUM
    if (usePremiumVoice && OPENAI_API_KEY) {
      setIsSpeaking(true); // Optimistic
      generateOpenAISpeech(text);
      return;
    }

    if (!voiceEnabled || !window.speechSynthesis) return;

    const utterance = new SpeechSynthesisUtterance(text);
    currentUtteranceRef.current = utterance; // [FIX] Store ref to prevent GC

    utterance.lang = language === 'fr' ? 'fr-FR' : 'en-US';

    // [NEW] CEFR Speed Adaptation
    const isBeginner = level && (level.toLowerCase().includes('beginner') || level.toLowerCase().includes('débutant') || level.toLowerCase().includes('a1') || level.toLowerCase().includes('a2'));
    if (isBeginner) {
      utterance.rate = 0.9; // Speak slower for beginners
    } else {
      utterance.rate = 1.0; // Natural speed
    }

    // Apply Selected Voice or Fallback
    if (selectedVoiceURI) {
      const voice = window.speechSynthesis.getVoices().find(v => v.voiceURI === selectedVoiceURI);
      if (voice) {
        utterance.voice = voice;
      } else {
        console.warn("Selected voice not found, using default.");
      }
    }

    // [FIX] Optimistic UI: Set Speaking immediately. 
    // If we wait for onstart, a browser lag makes it look like it failed.
    setIsSpeaking(true);

    utterance.onstart = () => {
      setIsSpeaking(true); // Confirm
      console.log("Audio Started");
    };

    // [NEW] Auto-Restart Mic after Speaking
    utterance.onend = () => {
      setIsSpeaking(false);
      currentUtteranceRef.current = null; // Cleanup ref

      if (isHandsFree) {
        // Small delay to prevent echo
        setTimeout(() => {
          setUserMessage(""); // Clear previous
          try {
            speechRecognitionRef.current?.start();
            console.log("Mic restarted automatically.");
          } catch (e) {
            console.log("Mic already started or error:", e);
          }
        }, 100);
      }
    };

    // [FIX] Force Resume (Chrome Bug)
    if (window.speechSynthesis.paused) {
      window.speechSynthesis.resume();
    } else {
      // Sometimes just calling it helps wake it up
      window.speechSynthesis.resume();
    }

    window.speechSynthesis.speak(utterance);

    // [FIX] Safety Timeout (in case onend never fires)
    // Estimate duration: ~150 words/min = 2.5 words/sec. 
    // Text length / 15 chars * 1000ms + buffer
    const estimatedTime = (text.length * 100) + 5000;
    setTimeout(() => {
      // [FIX] Must use REF, otherwise we check stale closure state!
      if (isSpeakingRef.current) {
        console.warn("Speech timed out, forcing reset.");
        setIsSpeaking(false);
        if (isHandsFree) {
          try { speechRecognitionRef.current?.start(); } catch (e) { }
        }
      }
    }, Math.max(estimatedTime, 10000)); // Min 10s wait

  };

  // Stop speaking when closed
  useEffect(() => {
    if (!isOpen) {
      window.speechSynthesis.cancel();
      audioPlayerRef.current?.pause(); // [NEW] Stop OpenAI
      setIsSpeaking(false);
      setIsHandsFree(false);
    }
  }, [isOpen]);

  // Empêche double déclenchement si ré-ouverture rapide
  const hasRunForThisOpen = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hasRunForThisOpen.current = false;
      setSuggestion(""); // Reset on close
      setMode("selection"); // Reset to selection on close
      setActiveRoleplay(null); // Reset roleplay
      setChatHistory([]); // [FIX] Reset history so auto-start works next time
      setLoading(false); // [FIX] Reset loading state
      setUserMessage(""); // Reset user input
    }
  }, [isOpen]);

  const SCENARIOS = [
    { id: 'interview', title: 'Job Interview', icon: Work, color: '#4f46e5', prompt: "You are a strict HR Manager conducting a job interview. I am the candidate. Ask me about my experience, strengths, and weaknesses. Be professional but tough." },
    { id: 'travel', title: 'Airport Check-in', icon: Flight, color: '#0ea5e9', prompt: "You are an airline check-in agent. I am a passenger checking in a flight to New York. Ask for my passport, bags, and seat preference." },
    { id: 'coffee', title: 'Coffee Shop Date', icon: LocalCafe, color: '#db2777', prompt: "You are meeting me for a first date at a coffee shop. Be friendly, ask personal questions, and use casual slang. Flirt a little bit." },
    { id: 'debate', title: 'Political Debate', icon: TheaterComedy, color: '#ef4444', prompt: "You disagree with me on everything. Pick a controversial topic (e.g., Pineapple on Pizza) and debate me passionately. Challenge my arguments." },
  ];

  // ---------- Firebase Functions callable ----------
  const functions = firebase.app().functions("us-central1");
  const callAiCoach = functions.httpsCallable("callAiCoach");

  // Charger les templates Firestore
  useEffect(() => {
    const fetchPrompts = async () => {
      try {
        const doc = await firebase
          .firestore()
          .collection("aiPromptTemplates")
          .doc("default")
          .get();
        if (doc.exists) {
          const data = doc.data();
          setPromptFr(data.promptFr || "");
          setPromptEn(data.promptEn || "");
          setPromptTutor(data.promptTutor || ""); // [NEW]
        }
      } catch (error) {
        console.error("Erreur chargement prompts Firestore:", error);
      }
    };
    fetchPrompts();
  }, []);

  const getLearningLogsForUser = async () => {
    const snapshot = await firebase
      .firestore()
      .collection("learningLogs")
      .where("userId", "==", userId)
      .get();
    return snapshot.docs.map((doc) => doc.data());
  };

  const getLastSuggestionsText = async (count = 3) => {
    const snapshot = await firebase
      .firestore()
      .collection("users")
      .doc(userId)
      .collection("coachInteractions")
      .where("type", "==", "suggestion")
      .orderBy("timestamp", "desc")
      .limit(count)
      .get();
    if (snapshot.empty) return null;

    return snapshot.docs
      .map((doc, i) => `${i + 1}. ${doc.data().messageSent}`)
      .join("\n");
  };

  const generatePromptFromLogs = (
    logs,
    sprintCountThisWeek,
    firstname,
    level,
    language,
    promptFr,
    promptEn
  ) => {
    const sortedLogs = logs
      .filter(
        (log) =>
          typeof log.discussion === "string" &&
          log.discussion.trim() !== ""
      )
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5);

    const discussions = sortedLogs
      .map((log, i) => `${i + 1}. ${log.date} : "${log.discussion}"`)
      .join("\n");

    let encouragement = "";
    if (language === "fr") {
      if (sprintCountThisWeek >= 4) {
        encouragement = `👏 Tu as participé à ${sprintCountThisWeek} sprints cette semaine. Bravo pour ta constance !`;
      } else if (sprintCountThisWeek >= 2) {
        encouragement = `💪 Tu es actif cette semaine avec ${sprintCountThisWeek} sprints. Continue comme ça !`;
      } else if (sprintCountThisWeek > 0) {
        encouragement = `👍 Tu as fait ${sprintCountThisWeek} sprint cette semaine. C’est un bon début, continue !`;
      } else {
        encouragement = `⏳ Tu n’as pas encore participé à un sprint cette semaine. Tu veux t’y remettre ?`;
      }
    } else {
      if (sprintCountThisWeek >= 4) {
        encouragement = `👏 You’ve done ${sprintCountThisWeek} sprints this week. Great consistency!`;
      } else if (sprintCountThisWeek >= 2) {
        encouragement = `💪 You're active this week with ${sprintCountThisWeek} sprints. Keep it up!`;
      } else if (sprintCountThisWeek > 0) {
        encouragement = `👍 You've done ${sprintCountThisWeek} sprint this week. Good start, keep going!`;
      } else {
        encouragement = `⏳ You haven’t done a sprint yet this week. Ready to jump back in?`;
      }
    }

    const template = language === "fr" ? promptFr : promptEn;
    if (!template) return "⚠️ Aucun template de prompt trouvé.";

    return (
      template
        .replace(/\{\{firstname\}\}/g, firstname || "l'élève")
        .replace(/\{\{level\}\}/g, level || "intermédiaire")
        .replace(/\{\{discussions\}\}/g, discussions)
        .replace(/\{\{encouragement\}\}/g, encouragement) +
      (language === "fr"
        ? "\n\nRéponds uniquement en français."
        : "\n\nRespond only in English.")
    );
  };

  const getSuggestion = async () => {
    try {
      setLoading(true);
      setSuggestion("");

      // --- LIMIT REMOVED BY USER REQUEST ---
      // Anti-spam 24h check deleted.

      const logs = await getLearningLogsForUser();
      const previousSuggestions = await getLastSuggestionsText(3);

      const now = new Date();
      const sprintCountThisWeek = (sprint || []).filter((ts) => {
        const date = ts?.toDate ? ts.toDate() : new Date(ts);
        const daysAgo = (now - date) / (1000 * 60 * 60 * 24);
        return daysAgo <= 7;
      }).length;

      let prompt = generatePromptFromLogs(
        logs,
        sprintCountThisWeek,
        firstname,
        level,
        language,
        promptFr,
        promptEn
      );

      if (previousSuggestions) {
        prompt += `\n\nVoici ce que tu as déjà proposé récemment à ${firstname} :\n${previousSuggestions}\n\nMerci d'éviter les répétitions et d'adapter ta prochaine suggestion.`;
      }

      // 🧠 Appel de la Cloud Function
      const systemInstruction =
        language === "fr"
          ? "Tu es un coach linguistique bienveillant pour adultes francophones qui améliorent leur anglais. Tu donnes des suggestions d'actions concrètes, petites et réalistes, alignées sur leurs objectifs et leur niveau."
          : "You are a kind language coach for French-speaking adults improving their English. You give concrete, small and realistic action suggestions aligned with their goals and level.";

      const result = await callAiCoach({
        systemInstruction,
        userPrompt: prompt,
      });

      let aiResponse = (result.data && result.data.text) || "";
      aiResponse = (aiResponse || "").toString().trim();

      // Clean markdown code blocks
      if (aiResponse.startsWith("```")) {
        aiResponse = aiResponse
          .replace(/^```(json)?/i, "")
          .replace(/```$/, "")
          .trim();
      }

      setSuggestion(aiResponse || "Pas de réponse générée.");

      const interactionRef = await logCoachInteraction(userId, {
        type: "suggestion",
        messageSent: aiResponse,
        responseExpected: true,
        handledBy: "AI",
      });

      if (interactionRef?.id) setLastInteractionId(interactionRef.id);
    } catch (err) {
      console.error("Erreur Cloud Function callAiCoach :", err);
      setSuggestion("Une erreur est survenue lors de la connexion au Coach.");
    } finally {
      setLoading(false);
    }
  };

  // Auto-start disabled because we want Selection Screen first
  /*
  useEffect(() => {
    if (isOpen && autoStart && !hasRunForThisOpen.current) {
      hasRunForThisOpen.current = true;
      getSuggestion();
    }
  }, [isOpen, autoStart]);
  */

  const startSuggestionMode = () => {
    setMode("suggestion");
    if (!suggestion) {
      getSuggestion();
    }
  };

  const startTutorMode = () => {
    setActiveRoleplay(null); // Clear any specific role
    setMode("tutor");
    setVoiceEnabled(true); // Force Voice ON
  };

  const startRoleplayMenu = () => {
    setMode("roleplay-menu");
  };

  const startRoleplay = (scenario) => {
    // [FIX] Force Clean Slate
    setChatHistory([]);
    setLoading(false);
    setIsSpeaking(false);
    setIsHandsFree(true); // [FIX] Auto-enable hands-free loop

    setActiveRoleplay(scenario);
    setMode("tutor");
    setVoiceEnabled(true); // Force Voice ON
  };

  // [NEW] Trigger Auto-Start when entering Tutor Mode
  useEffect(() => {
    // Ensure activeRoleplay is ready if we are in a roleplay mode
    // (If mode is tutor but activeRoleplay is null, wait? No, standard tutor doesn't use it)

    // Debug Log
    console.log(`[AutoStart Check] Mode: ${mode}, History: ${chatHistory.length}, Roleplay: ${activeRoleplay?.title}`);

    if (mode === 'tutor' && chatHistory.length === 0) {
      console.log(">>> TRIGGERING AUTO-START <<<");
      handleSendMessage("__AUTO_START__");
    }
  }, [mode, activeRoleplay]);

  const handleAcceptSuggestion = async () => {
    if (!lastInteractionId) return;
    try {
      await firebase
        .firestore()
        .collection("users")
        .doc(userId)
        .collection("coachInteractions")
        .doc(lastInteractionId)
        .update({ userResponse: "accepted", actionTaken: "accepted" });
      onClose();
    } catch (error) {
      console.error("Erreur mise à jour de la réponse :", error);
    }
  };

  const handleIgnoreSuggestion = async () => {
    if (!lastInteractionId) return;
    try {
      await firebase
        .firestore()
        .collection("users")
        .doc(userId)
        .collection("coachInteractions")
        .doc(lastInteractionId)
        .update({ userResponse: "ignored", actionTaken: "ignored" });
      onClose();
    } catch (error) {
      console.error("Erreur mise à jour ignore :", error);
    }
  };

  // --- TUTOR LOGIC ---
  // Modified to accept direct text arg for auto-send
  const handleSendMessage = async (manualText = null) => {
    console.log(`[handleSendMessage] Called with: ${manualText}, Loading: ${loading}, Speaking: ${isSpeaking}`);

    // [FIX] Clear any pending silence timer to prevent double-firing
    if (silenceTimer.current) clearTimeout(silenceTimer.current);

    if (loading || isSpeaking) {
      console.warn("[handleSendMessage] BLOCKED - AI is busy");
      return;
    }

    const textToSend = typeof manualText === 'string' ? manualText : userMessage;

    if (!textToSend || !textToSend.trim()) {
      console.warn("[handleSendMessage] BLOCKED - Empty text");
      return;
    }

    let newHistory = [...chatHistory];

    // Only add to history if not internal command
    const isAutoStart = textToSend === "__AUTO_START__";
    if (!isAutoStart) {
      const newMessage = { role: 'user', content: textToSend };
      newHistory = [...chatHistory, newMessage];
      setChatHistory(prev => [...prev, newMessage]);
    }

    setUserMessage(""); // Moved this line to after AI response is received
    setLoading(true);

    try {
      // Construct Prompt Context
      let systemInstruction = activeRoleplay
        ? activeRoleplay.prompt
        : promptTutor;

      // Default Fallback
      if (!systemInstruction) {
        systemInstruction = language === 'fr'
          ? "Tu es un professeur de langue expert. Ton élève s'appelle {{firstname}} et a un niveau {{level}}. Ton but est de le faire PARLER. Ne donne pas juste la réponse. Pose des questions, propose des exercices de prononciation ou de vocabulaire adaptés à son niveau. Corrige ses fautes gentiment."
          : "You are an expert language tutor. Your student is {{firstname}}, level {{level}}. Your goal is to get them SPEAKING. Do not just answer. Ask questions, propose pronunciation or vocabulary exercises suited to their level. Correct mistakes gently.";
      }


      // Inject Variables
      systemInstruction = systemInstruction
        .replace(/\{\{firstname\}\}/g, firstname || "Student")
        .replace(/\{\{level\}\}/g, level || "Intermediate");

      // [NEW] CEFR Level Specific Instructions
      const lvl = (level || "").toLowerCase();
      if (lvl.includes('beginner') || lvl.includes('débutant') || lvl.includes('a1') || lvl.includes('a2')) {
        systemInstruction += " \n[CEFR A1/A2]: The user is a BEGINNER. You MUST speak SLOWLY and CLEARLY. Use very SIMPLE vocabulary. Correct every mistake gently.";
      } else if (lvl.includes('intermediate') || lvl.includes('intermédiaire') || lvl.includes('b1') || lvl.includes('b2')) {
        systemInstruction += " \n[CEFR B1/B2]: The user is INTERMEDIATE. Speak naturally but clearly. Introduce common idioms. Correct grammar mistakes.";
      } else {
        systemInstruction += " \n[CEFR C1/C2]: The user is ADVANCED. Speak at a native, fluid pace. Use complex sentence structures and nuanced vocabulary. act like a native speaker.";
      }

      // [New] Enforce Short & Interactive
      systemInstruction += "\n\nIMPORTANT: Keep your answers SHORT (max 2 sentences). This is a voice conversation. ALWAYS end with a question or a challenge to keep the user talking.";

      // [FIX] Anti-Repetition Rule
      if (chatHistory.length > 0) {
        systemInstruction += " DO NOT START WITH 'Hi', 'Hello', or 'Bonjour'. DO NOT use the user's name in every sentence. Speak naturally.";
      }

      // [NEW] Introduction / Auto-Start Logic
      if (manualText === "__AUTO_START__") {
        systemInstruction += "\n\nIMPORTANT: YOU ARE STARTING THE CONVERSATION. DO NOT explain who you are. DO NOT say 'Here is the scenario'. JUST SAY YOUR FIRST LINE AS THE CHARACTER. Be direct.";
      }

      // Keep Context Strict
      if (language === 'fr') systemInstruction += " Réponds en français.";
      else systemInstruction += " Respond in English.";

      // Build history string for context (simplified)
      const historyText = newHistory.map(m => `${m.role === 'user' ? 'User' : 'AI'}: ${m.content}`).join('\n');

      // [FIX] Network Timeout Constraint
      // If the Cloud Function takes > 45s, we treat it as a timeout.
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error("Network Timeout")), 45000)
      );

      const result = await Promise.race([
        callAiCoach({
          systemInstruction,
          userPrompt: manualText === "__AUTO_START__"
            ? "Begin the roleplay now. Speak your first sentence."
            : `Context:\n${historyText}\n\nUser: ${textToSend}\nAI:`
        }),
        timeoutPromise
      ]);

      let aiResponse = (result.data && result.data.text) || "";
      aiResponse = (aiResponse || "").toString().trim();

      if (!aiResponse) {
        aiResponse = "Sorry, I am confused. Can you repeat?";
      }

      setChatHistory(prev => [...prev, { role: 'ai', content: aiResponse }]);

      // Speak response
      if (mode === 'tutor') {
        console.log("Speaking Response:", aiResponse); // [DEBUG]
        speakText(aiResponse);
      }

    } catch (err) {
      console.error("Error in Tutor Mode:", err);
      // [FIX] Speak specific error to debug
      if (!isSpeakingRef.current) {
        let errorMsg = language === 'fr' ? "Erreur de connexion." : "Connection Error.";

        if (err.message === "Network Timeout") {
          errorMsg = language === 'fr' ? "Trop long, je réessaie." : "Timed out, retrying.";
        } else {
          // Debug: Speak the error type if possible or generic
          // errorMsg = "System Error: " + err.message; // Too technical for user?
          errorMsg = language === 'fr' ? "Une erreur est survenue." : "An error occurred.";
          // SHOW ERROR ON SCREEN FOR DEBUG
          setUserMessage("Error: " + err.message);
        }

        speakText(errorMsg);
      }
    } finally {
      setLoading(false);
      loadingRef.current = false; // Sync Ref immediately
    }
  };

  // --- CLEANUP ON UNMOUNT OR CLOSE ---
  useEffect(() => {
    if (!isOpen) {
      console.log("Closing: Stopping all audio/mic.");
      // 1. Stop Speech Synths
      window.speechSynthesis.cancel();
      setIsSpeaking(false);
      isSpeakingRef.current = false;

      // 2. Stop Recognition
      if (speechRecognitionRef.current) {
        try {
          speechRecognitionRef.current.abort(); // Abort is faster/harder than stop
        } catch (e) { }
      }
      setIsListening(false);
      setIsHandsFree(false);
      setLoading(false);
    }
  }, [isOpen]);

  // --- Render ---
  // [NEW] PERMISSION CHECK
  const hasAccess = (profile?.products && profile.products.includes('ai_coach')) ||
    (profile?.role && (profile.role.includes('admin') || profile.role.includes('professor')));

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: "transparent",
          boxShadow: "none",
        },
      }}
    >
      <GlassDialogPanel>
        {/* Header / Close Button */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} zIndex={10}>
          <Stack direction="row" spacing={1} alignItems="center">
            {mode === 'selection' && <AutoAwesome sx={{ color: BRAND_GREEN, fontSize: 28 }} />}
            {mode === 'suggestion' && <AutoAwesome sx={{ color: BRAND_GREEN, fontSize: 28 }} />}
            {mode === 'tutor' && <School sx={{ color: '#00baee', fontSize: 28 }} />}
            {mode === 'roleplay-menu' && <TheaterComedy sx={{ color: '#db2777', fontSize: 28 }} />}

            <Typography variant="h6" fontWeight={700} color="#fff">
              {mode === 'selection' && "Choose your Mode"}
              {mode === 'suggestion' && "Ellipse Coach"}
              {mode === 'tutor' && (activeRoleplay ? activeRoleplay.title : "Language Instructor")}
              {mode === 'roleplay-menu' && "Roleplay Arcade"}
            </Typography>
          </Stack>

          <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.7)" }}>
            <Close />
          </IconButton>
        </Box>

        {!hasAccess ? (
          // --- LOCKED STATE ---
          <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center">
            <Box p={4} borderRadius={4} bgcolor="rgba(0,0,0,0.4)" backdropFilter="blur(10px)" border={`1px solid ${BRAND_GREEN}`}>
              <SmartToy sx={{ fontSize: 60, color: '#555', mb: 2 }} />
              <Typography variant="h5" fontWeight="bold" color="#fff" gutterBottom>
                Feature Locked
              </Typography>
              <Typography variant="body1" color="rgba(255,255,255,0.7)" mb={3}>
                You need the <strong>Arcade AI Access</strong> pass to use this feature.
              </Typography>
              <Typography variant="caption" color="rgba(255,255,255,0.5)">
                Please contact your administrator to upgrade your account.
              </Typography>
            </Box>
          </Box>
        ) : (
          // --- UNLOCKED CONTENT (Existing) ---
          <Box flex={1} display="flex" flexDirection="column" position="relative" overflow="hidden">

            {/* Content Switch */}
            {mode === 'selection' ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center" alignItems="center" height="100%" py={4}>
                {/* OPTION 1: COACH */}
                <Box
                  onClick={startSuggestionMode}
                  sx={{
                    flex: 1,
                    width: '100%',
                    height: '240px',
                    p: 3,
                    borderRadius: 4,
                    border: `2px solid transparent`,
                    background: 'rgba(255,255,255,0.4)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      background: 'rgba(176, 213, 11, 0.15)',
                      borderColor: BRAND_GREEN,
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <Stack alignItems="center" spacing={2} textAlign="center" height="100%" justifyContent="center">
                    <Box sx={{ p: 2, borderRadius: '50%', bgcolor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                      <AutoAwesome sx={{ fontSize: 32, color: BRAND_GREEN }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={800} color="#1f2937">Get Coached</Typography>
                      <Typography variant="caption" color="#6b7280" display="block">Motivation & Tips</Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* OPTION 2: TUTOR */}
                <Box
                  onClick={startTutorMode}
                  sx={{
                    flex: 1,
                    width: '100%',
                    height: '240px',
                    p: 3,
                    borderRadius: 4,
                    border: `2px solid transparent`,
                    background: 'rgba(255,255,255,0.4)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      background: 'rgba(0, 186, 238, 0.15)',
                      borderColor: '#00baee',
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <Stack alignItems="center" spacing={2} textAlign="center" height="100%" justifyContent="center">
                    <Box sx={{ p: 2, borderRadius: '50%', bgcolor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                      <ChatBubble sx={{ fontSize: 32, color: '#00baee' }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={800} color="#1f2937">Practice</Typography>
                      <Typography variant="caption" color="#6b7280" display="block">Open Conversation</Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* OPTION 3: ROLEPLAY */}
                <Box
                  onClick={startRoleplayMenu}
                  sx={{
                    flex: 1,
                    width: '100%',
                    height: '240px',
                    p: 3,
                    borderRadius: 4,
                    border: `2px solid transparent`,
                    background: 'rgba(255,255,255,0.4)',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      background: 'rgba(219, 39, 119, 0.15)',
                      borderColor: '#db2777',
                      transform: 'translateY(-4px)'
                    }
                  }}
                >
                  <Stack alignItems="center" spacing={2} textAlign="center" height="100%" justifyContent="center">
                    <Box sx={{ p: 2, borderRadius: '50%', bgcolor: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                      <TheaterComedy sx={{ fontSize: 32, color: '#db2777' }} />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight={800} color="#1f2937">Roleplay Arcade</Typography>
                      <Typography variant="caption" color="#6b7280" display="block">Real-life Scenarios</Typography>
                    </Box>
                  </Stack>
                </Box>
              </Stack>
            ) : mode === 'roleplay-menu' ? (
              <Box height="100%" overflow="auto" p={2}>
                <Typography variant="h6" align="center" gutterBottom fontWeight={800} mb={3}>Choose a Scenario</Typography>
                <Grid container spacing={2}>
                  {SCENARIOS.map((scene) => (
                    <Grid item xs={12} sm={6} key={scene.id}>
                      <Box
                        onClick={() => startRoleplay(scene)}
                        sx={{
                          p: 2,
                          borderRadius: 3,
                          bgcolor: 'rgba(255,255,255,0.6)',
                          cursor: 'pointer',
                          transition: 'transform 0.2s',
                          border: '1px solid transparent',
                          '&:hover': { transform: 'scale(1.02)', borderColor: scene.color, boxShadow: `0 4px 12px ${scene.color}33` }
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <Box p={1} borderRadius={2} bgcolor={`${scene.color}22`}>
                            <scene.icon sx={{ color: scene.color }} />
                          </Box>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700}>{scene.title}</Typography>
                            <Typography variant="caption" color="text.secondary">Starting at level {level}</Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ) : mode === 'suggestion' ? (
              <>
                <Box flex={1} display="flex" flexDirection="column" alignItems="center" justifyContent="center" px={2}>
                  {loading ? (
                    <Box textAlign="center" py={4}>
                      <BrainPulse
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [0.8, 1, 0.8]
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                      >
                        <SmartToy sx={{ color: 'white', fontSize: 32 }} />
                      </BrainPulse>
                      <Typography variant="h6" fontWeight={700} color="#4b5563" gutterBottom>
                        Analyzing your progress...
                      </Typography>
                      <Typography variant="body2" color="#6b7280">
                        Crafting a personalized plan for you.
                      </Typography>
                    </Box>
                  ) : (
                    <Fade in={!loading}>
                      <Box width="100%">
                        <Typography
                          variant="body1"
                          fontSize={18}
                          color="#374151"
                          lineHeight={1.8}
                          sx={{ whiteSpace: "pre-wrap" }}
                        >
                          {suggestion}
                        </Typography>
                      </Box>
                    </Fade>
                  )}
                </Box>

                {/* Footer Actions */}
                {!loading && suggestion && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="flex-end" mt={4}>
                    <SecondaryButton
                      startIcon={<Cancel />}
                      onClick={handleIgnoreSuggestion}
                    >
                      Ignore
                    </SecondaryButton>
                    <GradientButton
                      startIcon={<CheckCircle />}
                      onClick={handleAcceptSuggestion}
                    >
                      I'll do it
                    </GradientButton>
                  </Stack>
                )}
              </>
            ) : (
              // --- TUTOR INTERFACE (ORB UI) ---
              <Box display="flex" flexDirection="column" height="500px" justifyContent="center" alignItems="center" position="relative">

                {/* The Orb */}
                <Box position="relative" zIndex={10} onClick={toggleListening}>
                  <Orb state={loading ? 'loading' : isListening ? 'listening' : isSpeaking ? 'speaking' : 'idle'} />

                  {/* Icon Inside Orb */}
                  <Box position="absolute" top="50%" left="50%" sx={{ transform: 'translate(-50%, -50%)', opacity: 0.7, pointerEvents: 'none' }}>
                    {loading ? <CircularProgress color="inherit" size={40} /> :
                      isListening ? <Mic sx={{ color: 'white', fontSize: 40 }} /> :
                        isSpeaking ? <VolumeUp sx={{ color: 'white', fontSize: 40 }} /> :
                          activeRoleplay ? <activeRoleplay.icon sx={{ color: 'white', fontSize: 40 }} /> :
                            <SmartToy sx={{ color: 'white', fontSize: 40 }} />}
                  </Box>
                </Box>

                {/* Live Transcript */}
                <Box width="100%" display="flex" justifyContent="center" position="absolute" bottom="80px" zIndex={20} sx={{ pointerEvents: 'none' }}>
                  <Fade in={!!userMessage || isSpeaking}>
                    <TranscriptBox>
                      <Typography variant="h6" fontWeight={700} color="#1f2937">
                        {userMessage || (chatHistory.length > 0 && chatHistory[chatHistory.length - 1].content)}
                      </Typography>
                    </TranscriptBox>
                  </Fade>
                </Box>

                {/* Status Text */}
                <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', bottom: '20px' }}>
                  {isListening ? "Listening..." : isSpeaking ? "Speaking..." : "Tap the Orb to pause/resume"}
                </Typography>

                {/* Hands Free Indicator */}
                {isHandsFree && (
                  <Box position="absolute" top="20px" right="20px">
                    <Box sx={{ px: 2, py: 0.5, borderRadius: 10, bgcolor: 'rgba(0, 186, 238, 0.2)', border: '1px solid #00baee' }}>
                      <Typography variant="caption" fontWeight={700} color="#007bb5">HANDS FREE</Typography>
                    </Box>
                  </Box>
                )}

                {/* [NEW] DEBUG / Level Indicator */}
                <Box position="absolute" top="60px" right="20px">
                  <Box sx={{ px: 2, py: 0.5, borderRadius: 10, bgcolor: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.1)' }}>
                    <Typography variant="caption" fontWeight={600} color="#6b7280">
                      {level || "Unknown"}
                      {(level?.includes('Beginner') || level?.includes('Débutant')) ? " (A1/A2)" :
                        (level?.includes('Intermediate') || level?.includes('Intermédiaire')) ? " (B1/B2)" :
                          " (C1/C2)"}
                    </Typography>
                  </Box>
                </Box>

                {/* [NEW] DEBUG OVERLAY (Bottom Right) */}
                <Box position="absolute" bottom="80px" right="20px" textAlign="right" sx={{ opacity: 0.5, pointerEvents: 'none' }}>
                  <Typography variant="caption" display="block" color="text.secondary">Mode: {mode}</Typography>
                  <Typography variant="caption" display="block" color="text.secondary">State: {isSpeaking ? "SPEAKING" : isListening ? "LISTENING" : loading ? "LOADING" : "IDLE"}</Typography>
                  <Typography variant="caption" display="block" color="text.secondary">Voice: {selectedVoiceURI ? "Custom" : "Default"}</Typography>
                </Box>

                {/* Volume Toggle [RESTORED] */}
                {/* Moved slightly to avoid overlap */}
                <Box position="absolute" top="20px" left="20px" display="flex" gap={1}>
                  <IconButton
                    onClick={(e) => { e.stopPropagation(); setVoiceEnabled(!voiceEnabled); }}
                    sx={{ bgcolor: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(4px)' }}
                  >
                    {voiceEnabled ? <VolumeUp sx={{ color: '#1f2937' }} /> : <VolumeOff sx={{ color: '#9ca3af' }} />}
                  </IconButton>

                  {/* Voice Selector Cog */}
                  <IconButton
                    onClick={(e) => { e.stopPropagation(); setVoiceMenuAnchor(e.currentTarget); }}
                    sx={{ bgcolor: 'rgba(255,255,255,0.4)', backdropFilter: 'blur(4px)' }}
                  >
                    <Settings sx={{ color: '#1f2937' }} />
                  </IconButton>

                  <Menu
                    anchorEl={voiceMenuAnchor}
                    open={Boolean(voiceMenuAnchor)}
                    onClose={() => setVoiceMenuAnchor(null)}
                    PaperProps={{ sx: { bgcolor: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', borderRadius: 4, minWidth: 200 } }}
                  >
                    {/* [NEW] Premium Toggle */}
                    <MenuItem>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" width="100%">
                        <Typography variant="body2" fontWeight="bold">Premium Voice</Typography>
                        <Switch
                          checked={usePremiumVoice}
                          onChange={(e) => setUsePremiumVoice(e.target.checked)}
                          color="secondary"
                          size="small"
                        />
                      </Stack>
                    </MenuItem>

                    <Box height={1} bgcolor="#e5e7eb" my={1} />

                    {availableVoices.length === 0 && <MenuItem disabled>No voices found</MenuItem>}
                    {availableVoices.map(v => (
                      <MenuItem
                        key={v.voiceURI}
                        selected={v.voiceURI === selectedVoiceURI}
                        onClick={() => handleVoiceChange(v.voiceURI)}
                      >
                        {v.name}
                      </MenuItem>
                    ))}
                  </Menu>
                </Box>

              </Box>
            )}
          </Box>
        )}
      </GlassDialogPanel>
    </Dialog >
  );
};

const mapState = (state) => ({
  profile: state.firebase.profile, // [NEW] Get profile from Redux
});

export default connect(mapState)(AiCoach);
